// frontend/packages/business-console/src/pages/AnalyticsAlertsSettings.jsx
//
// Client-facing analytics alerts (analytics-intelligence.md, ask #3). This is
// the tenant's opt-in surface for the derived, celebratory/noteworthy alerts:
// a lead spike (a good day, reply fast), a sustained lead drop, and a
// best-week-on-record nudge. It writes settings.analyticsAlerts via the
// existing PATCH /businesses/settings endpoint (whitelist validator).
//
// OFF by default: the master toggle (settings.analyticsAlerts.enabled) is
// false until the tenant flips it on here. Only then does the nightly sweep
// email them (and only if the platform-level SITE_SIGNALS_CLIENT_ALERTS_ENABLED
// switch is on). Per-rule flags let them pick which alerts; the optional email
// field overrides the default recipient. Email only for v1 (SMS is a future
// channel, deliberately not offered here yet).
//
// Role: the endpoint is businessadmin-only server-side. Like the sibling
// settings pages, the AuthContext does not expose role client-side, so we
// render the controls for everyone and surface the server 403 if a non-admin
// tries to save (then leave state unchanged since it reads from `business`).

import React, { useContext, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

import { BusinessContext } from '../contexts/BusinessContext';
import { updateBusinessSettings } from '../services/businessService';
import { useTokens, PageHeader, Surface } from '../components/primitives';

// The per-rule alert toggles, in display order. `key` matches the schema flag
// on settings.analyticsAlerts and the whitelist validator.
const ALERT_RULES = [
  {
    key: 'leadSpike',
    label: 'Lead spike',
    desc: 'A day with far more form leads than your usual. A heads-up to reply fast while interest is hot.',
  },
  {
    key: 'leadDrop',
    label: 'Lead drop',
    desc: 'A sustained decline in leads versus your recent baseline, so a quiet stretch does not go unnoticed.',
  },
  {
    key: 'bestWeek',
    label: 'Best week on record',
    desc: 'A celebratory note when your site has its best week of sessions or leads so far.',
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AnalyticsAlertsSettings() {
  const t = useTokens();
  const { business, loading, refreshBusiness } = useContext(BusinessContext);

  const alerts = useMemo(
    () => business?.settings?.analyticsAlerts || {},
    [business]
  );
  // OFF by default: only an explicit `true` enables.
  const enabled = alerts.enabled === true;
  // Per-rule flags default ON once alerts are enabled (schema default true);
  // treat absent as on so the display matches the sweep.
  const ruleOn = (key) => alerts[key] !== false;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);

  // Local editable state for the override email (only committed on Save).
  const [emailDraft, setEmailDraft] = useState(null); // null = not editing
  const currentEmail = alerts.email || '';
  const emailValue = emailDraft === null ? currentEmail : emailDraft;
  const emailInvalid = emailValue.trim() !== '' && !EMAIL_RE.test(emailValue.trim());

  const patch = async (payload, successMsg) => {
    setSaving(true);
    setError(null);
    try {
      await updateBusinessSettings({ analyticsAlerts: payload });
      await refreshBusiness();
      if (successMsg) setSavedMsg(successMsg);
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      setError(
        status === 403
          ? serverMsg || 'Only business admins can change alert settings.'
          : serverMsg || 'Could not save. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMaster = (event) => {
    const next = event.target.checked;
    patch(
      { enabled: next },
      next
        ? 'Analytics alerts turned on.'
        : 'Analytics alerts turned off.'
    );
  };

  const handleToggleRule = (key) => (event) => {
    patch({ [key]: event.target.checked });
  };

  const handleSaveEmail = () => {
    const trimmed = emailValue.trim();
    if (trimmed !== '' && !EMAIL_RE.test(trimmed)) return;
    patch(
      { email: trimmed },
      trimmed
        ? 'Alert email updated.'
        : 'Alert email cleared. Using your default contact address.'
    ).then(() => setEmailDraft(null));
  };

  if (loading && !business) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 880, mx: 'auto', width: '100%' }}>
      <PageHeader title="Analytics alerts" sx={{ mb: 0.75 }} />
      <Typography sx={{ fontSize: 13.5, color: t.muted, mb: 3 }}>
        Get a heads-up by email when something noteworthy happens with your
        website: a spike in leads, a quiet stretch, or your best week yet. Off
        by default, and you choose which alerts you want.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Surface sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <InsightsOutlinedIcon
            fontSize="small"
            sx={{ color: 'text.secondary', mt: 0.5 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FormControlLabel
              sx={{ m: 0, alignItems: 'center' }}
              control={
                <Switch
                  checked={enabled}
                  onChange={handleToggleMaster}
                  disabled={saving}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Email me analytics alerts
                </Typography>
              }
            />
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.5 }}
            >
              The master switch. When on, we email you as the alerts below fire.
              When off, you get none of them.
            </Typography>
          </Box>
        </Stack>

        <Collapse in={enabled} unmountOnExit>
          <Divider sx={{ my: 2 }} />

          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              letterSpacing: '.5px',
              textTransform: 'uppercase',
              color: t.faint,
            }}
          >
            Which alerts
          </Typography>

          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {ALERT_RULES.map((rule) => (
              <Box key={rule.key}>
                <FormControlLabel
                  sx={{ m: 0, alignItems: 'center' }}
                  control={
                    <Switch
                      size="small"
                      checked={ruleOn(rule.key)}
                      onChange={handleToggleRule(rule.key)}
                      disabled={saving}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {rule.label}
                    </Typography>
                  }
                />
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', ml: 4.5, mt: -0.25 }}
                >
                  {rule.desc}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              letterSpacing: '.5px',
              textTransform: 'uppercase',
              color: t.faint,
            }}
          >
            Send to
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', mt: 0.75, mb: 1.5 }}
          >
            Leave blank to use your business&apos;s default contact email.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              type="email"
              placeholder="alerts@yourbusiness.com"
              value={emailValue}
              onChange={(e) => setEmailDraft(e.target.value)}
              error={emailInvalid}
              helperText={emailInvalid ? 'Enter a valid email address.' : ' '}
              sx={{ flex: 1, minWidth: 0 }}
              disabled={saving}
            />
            <Box>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSaveEmail}
                disabled={
                  saving ||
                  emailInvalid ||
                  emailValue.trim() === currentEmail.trim()
                }
                sx={{ mt: 0.5 }}
              >
                Save email
              </Button>
            </Box>
          </Stack>
        </Collapse>

        {saving ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 2 }}
          >
            <CircularProgress size={14} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Saving...
            </Typography>
          </Stack>
        ) : null}

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Alerts are informational and sent at most once per period, so we
          never flood your inbox. Only admins can change these settings.
        </Typography>
      </Surface>

      <Snackbar
        open={Boolean(savedMsg)}
        autoHideDuration={3000}
        onClose={() => setSavedMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSavedMsg(null)}
        >
          {savedMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
